import React, { useState, useEffect } from "react";
import { useSubscription } from "../../hooks/useSubscription";
import SubscriptionPlans from "./SubscriptionPlans";
import SubscriptionConfirmModal from "./SubscriptionConfirmModal";
import "../../styles/SubscriptionManager.css";

const SubscriptionManager = ({ user }) => {
  const { plans, subscription, setSubscription } = useSubscription(user);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [message, setMessage] = useState(null);
  const [sortedPlans, setSortedPlans] = useState([]);
  const [paymentStatus, setPaymentStatus] = useState(null);

  // Ordena os planos para que o atual fique em primeiro (opcional, mas bom para UX)
  useEffect(() => {
    if (plans.length > 0) {
      const currentPlanId = subscription?.plan?.id;
      
      if (currentPlanId) {
        const sorted = [...plans].sort((a, b) => {
          if (a.id === currentPlanId) return -1;
          if (b.id === currentPlanId) return 1;
          return 0;
        });
        setSortedPlans(sorted);
      } else {
        setSortedPlans(plans);
      }
    }
  }, [plans, subscription]);

  const handleSubscribe = (plan) => {
    // Se for o plano atual, não faz nada ou poderia abrir detalhes
    if (subscription?.plan?.id === plan.id) return;
    if (selectedPlan) return;
    
    setSelectedPlan(plan);
  };

  const closeModal = () => {
    setSelectedPlan(null);
    setMessage(null);
    setPaymentStatus(null);
  };

  // Função auxiliar para chamada de API
  const apiCall = async (endpoint, body) => {
    const response = await fetch(`https://api.rondago.com.br/api/${endpoint}`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    });
    return response.json();
  };

  const handleSubscribeConfirm = async (plan, cardData) => {
    if (isProcessing) return;
    setIsProcessing(true);
    setMessage(null);
    setPaymentStatus(null);

    try {
      console.log("📦 Gerando token...");
      const tokenResult = await apiCall("createCardToken", cardData);
      
      if (!tokenResult.card_token_id) throw new Error("Erro ao gerar token do cartão.");

      console.log("🚀 Atualizando assinatura...");
      const updateResult = await apiCall("updateSubscription", {
        user_id: user.uid,
        new_plan_id: plan.id,
        card_token_id: tokenResult.card_token_id,
        payer_email: user.email,
      });

      if (!updateResult.subscription_id) throw new Error("Erro ao atualizar assinatura.");

      // Atualiza estado local
      if (typeof setSubscription === "function") {
        setSubscription((prev) => ({
          ...prev,
          plan: {
            id: plan.id,
            name: plan.name,
            price: plan.price,
            status: "active", // Assumindo ativo após sucesso imediato ou 'in_process'
          },
          mercadoPagoSubscriptionId: updateResult.subscription_id,
        }));
      }

      setPaymentStatus(updateResult.payment_status || "approved");

    } catch (error) {
      console.error("❌ Erro:", error);
      setMessage("Não foi possível processar o pagamento. Verifique os dados do cartão.");
      setPaymentStatus("rejected");
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <div className="subscription-page-wrapper">
      <div className="subscription-container">
        <header className="subscription-header">
          <h1>Gerenciar Assinatura</h1>
          <p>Escolha o plano ideal para a gestão do seu condomínio</p>
        </header>

        {/* Nota: Certifique-se de que o componente SubscriptionPlans 
           aplica a classe 'current-plan-card' quando (plan.id === subscription.plan.id)
        */}
        <SubscriptionPlans 
          plans={sortedPlans} 
          subscription={subscription} 
          setSubscription={setSubscription} 
          onSelectPlan={handleSubscribe}
        />

        {selectedPlan && (
          <SubscriptionConfirmModal 
            selectedPlan={selectedPlan} 
            user={user} 
            currentPlan={subscription?.plan} 
            onConfirm={handleSubscribeConfirm}
            onClose={closeModal} 
            isProcessing={isProcessing} 
            message={message} 
            paymentStatus={paymentStatus} 
          />
        )}
      </div>
    </div>
  );
};

export default SubscriptionManager;